/*
 * Author: Harper Yan
 * File Created: Sunday, 24-09-01 10:51 am
 * Last Modified: Tuesday, 25-05-27 10:28 am, by Dw
 */

import BigNumber from 'bignumber.js';
import memoize from 'lodash/memoize';

import { getSafeRegex } from './regex';

interface UtilsNumberFormat extends Partial<BigNumber.Format> {
  removeZero?: boolean;
}

export const BIG_NUMBER_FORMAT: BigNumber.Format = {
  // string to prepend
  prefix: '',
  // decimal separator
  decimalSeparator: '.',
  // grouping separator of the integer part
  groupSeparator: ',',
  // primary grouping size of the integer part
  groupSize: 3,
  // secondary grouping size of the integer part
  secondaryGroupSize: 0,
  // grouping separator of the fraction part
  fractionGroupSeparator: ' ',
  // grouping size of the fraction part
  fractionGroupSize: 0,
  // string to append
  suffix: '',
};

export const ACCURACY_DECIMAL = 6;
export const GROSS_DECIMAL = 2;
export const CNY = 'CNY';
export const USD = 'USD';
export const DASH = '–';
const POSITIVE_SYMBOL = '+';
const NEGATIVE_SYMBOL = '-';

// memoize for performance, avoids creating a RegExp each call
export const getFormatNumberRegexp = memoize(
  (n: any, x: any) =>
    getSafeRegex(`\\d(?=(\\d{${x || 3}})+${n > 0 ? '\\.' : '$'})`, 'g'),
  (...args: string[]) => args.join(',')
);

export const formatNumber = (
  num: string | number = 0,
  n: any,
  x = 3
): string => {
  num = typeof num === 'number' ? num : +num;
  const re = getFormatNumberRegexp(n, x);
  return num.toFixed(Math.max(0, ~~n)).replace(re, '$&,');
};

/**
 * big number division
 * @param { BigNumber.Value } num molecular
 * @param { BigNumber.Value } base denominator
 * @returns { BigNumber } a / b
 */
export function divided(
  num: BigNumber.Value,
  base: BigNumber.Value
): BigNumber {
  return new BigNumber(num).dividedBy(base);
}

/**
 * big number add
 * @param { BigNumber.Value } args  A minimum of two parameters are required. support multiple parameters
 * @returns { BigNumber }
 */

export function plus(info: Array<BigNumber.Value>): BigNumber;
export function plus(
  base: BigNumber.Value,
  num: BigNumber.Value,
  ...args: Array<BigNumber.Value>
): BigNumber;
export function plus(...data: any[]): BigNumber {
  const [base, num, ...args] = data;
  if (Array.isArray(base)) {
    return base.reduce(
      (a: BigNumber, b) => a.plus(b || 0),
      new BigNumber(0)
    ) as BigNumber;
  }
  if (num === undefined) return new BigNumber(base);
  const res = new BigNumber(base).plus(num);
  return args.reduce((a: BigNumber, b) => a.plus(b || 0), res) as BigNumber;
}

/**
 * big number subtraction
 * @param num a
 * @param base b
 * @returns a - b
 */
export function minus(a: BigNumber.Value, b: BigNumber.Value): BigNumber {
  return new BigNumber(a).minus(b);
}

/**
 * big number multiplication
 * @param args A minimum of two parameters are required. support multiple parameters
 * @returns  BigNumber
 */
export const multiply = (
  base: BigNumber.Value,
  num: BigNumber.Value,
  ...args: Array<BigNumber.Value>
): BigNumber => {
  if (num === undefined) return new BigNumber(base);
  const res = new BigNumber(base).multipliedBy(num);
  return args.reduce(
    (a: BigNumber, b) => a.multipliedBy(b || 0),
    res
  ) as BigNumber;
};

export const safeBigNumber2Str = (num: BigNumber.Value): BigNumber =>
  divided(num, '1.00000000000000000000');

/**
 * Remove trailing zeros of a BigNumber, supports up tp 8 decimals
 * @param rawNumber input
 */
export function removeTrailingZero(rawNumber: BigNumber.Value, dp = 8): string {
  return new BigNumber(rawNumber).decimalPlaces(dp).toString();
}

/**
 * Returns the number of decimal places in a number.
 * @param {BigNumber.Value} num
 * @returns { number } length of decimal
 */
export const getPrecision = (num: BigNumber.Value): number =>
  new BigNumber(num).dp() as number;

export const getRealDecimalPlaces = (
  num: BigNumber.Value,
  decimalPlaces: number,
  removeZero = true
): number =>
  removeZero ? Math.min(getPrecision(num), decimalPlaces) : decimalPlaces;

/**
 * format a number to string, without trailing zero
 * @param rawNumber input
 */
export function numberToString(rawNumber: BigNumber.Value): string {
  return new BigNumber(rawNumber).toString(10);
}

/**
 * Intercept decimal
 * @param num Target value
 * @param decimalPlaces Accuracy value. default retains 2 decimal places
 * @param removeZero need to remove trailing zero. default is true
 * @example
 * x = 3.4561
 * toIntercept(x)      // '3.45'
 * toIntercept(x, 1)      // '3.4'
 * y = -3.4561
 * toIntercept(x, 1)      // '-3.4'
 * toIntercept(3.4000, 2)      // '3.4'
 * toIntercept(3.4000, 2, true)      // '3.40'
 * @returns { string } result
 */
export const toIntercept = (
  num: BigNumber.Value,
  decimalPlaces = 2,
  removeZero = true
): string => {
  if (!num && num !== 0) return '';
  // const dc = getRealDecimalPlaces(num, decimalPlaces, removeZero)
  // return new BigNumber(num).toFixed(dc, 1)
  const res = new BigNumber(num).toFixed(decimalPlaces, 1);
  const dc = removeZero && getRealDecimalPlaces(num, decimalPlaces, removeZero);
  return typeof dc === 'number' && dc < decimalPlaces
    ? toIntercept(res, dc, false)
    : res;
};

/**
 * Digital upward interception
 * @param num Target value
 * @param decimalPlaces Accuracy value. default retains 2 decimal places
 * @param removeZero need to remove trailing zero. default is true
 * @example
 * x = 3.456
 * toFixedCeil(x)      // '3.46'
 * toFixedCeil(x, 1)      // '3.5'
 * @returns { string } result
 */
export const toFixedCeil = (
  num: BigNumber.Value,
  decimalPlaces = 2,
  removeZero = true
): string => {
  if (!num && num !== 0) return '';
  // const dc = getRealDecimalPlaces(num, decimalPlaces, removeZero)
  // return new BigNumber(num).toFixed(dc, 2)
  const res = new BigNumber(num).toFixed(decimalPlaces, 2);
  const dc = removeZero && getRealDecimalPlaces(num, decimalPlaces, removeZero);
  return typeof dc === 'number' && dc < decimalPlaces
    ? toIntercept(res, dc, false)
    : res;
};

/**
 * Digital downward interception
 * @param num Target value
 * @param decimalPlaces Accuracy value. default retains 2 decimal places
 * @param removeZero need to remove trailing zero. default is true
 * @example
 * x = 3.456
 * toFixedFloor(x)      // '3.45'
 * toFixedFloor(x, 1)      // '3.4'
 * @returns { string } result
 */
export const toFixedFloor = (
  num: BigNumber.Value,
  decimalPlaces = 2,
  removeZero = true
): string => {
  if (!num && num !== 0) return '';
  // const dc = getRealDecimalPlaces(num, decimalPlaces, removeZero)
  // return new BigNumber(num).toFixed(dc, 3)
  const res = new BigNumber(num).toFixed(decimalPlaces, 3);
  const dc = removeZero && getRealDecimalPlaces(num, decimalPlaces, removeZero);
  return typeof dc === 'number' && dc < decimalPlaces
    ? toIntercept(res, dc, false)
    : res;
};

/**
 * Rounding of numbers
 * @param num Target value
 * @param decimalPlaces Accuracy value. default retains 2 decimal places
 * @param removeZero need to remove trailing zero. default is true
 * @example
 * x = 3.4561
 * toFixedCeil(x)      // '3.46'
 * toFixedCeil(x, 1)      // '3.5'
 * toFixedCeil(x, 3)      // '3.456'
 * @returns { string } result
 */
export const toFixedRound = (
  num: BigNumber.Value,
  decimalPlaces = 2,
  removeZero = true
): string => {
  if (!num && num !== 0) return '';
  // const dc = getRealDecimalPlaces(num, decimalPlaces, removeZero)
  // return new BigNumber(num).toFixed(dc, 4)
  const res = new BigNumber(num).toFixed(decimalPlaces, 4);
  const dc = removeZero && getRealDecimalPlaces(num, decimalPlaces, removeZero);
  return typeof dc === 'number' && dc < decimalPlaces
    ? toIntercept(res, dc, false)
    : res;
};

/**
 * To format number. toFormat(v, [dp [, rm[, format]]]) ⇒ string
 * @example
 * toFormat(12345.34567)
 * // 12,345.34567  just to kilobits. and can't intercept decimalPlaces
 *
 * toFormat(12345.34567, 2)
 * // 12,345.35  If roundingMode is null. use 4 (ROUND_HALF_UP) instead
 *
 * toFormat(12345.34567, 2, 1)
 * // 12,345.34  If has roundingMode props. Same as toFormat(toIntercept(12345.34567))

 * const fmt = {
    // string to prepend
    prefix: '',
    // decimal separator
    decimalSeparator: '.',
    // grouping separator of the integer part
    groupSeparator: ',',
    // primary grouping size of the integer part
    groupSize: 3,
    // secondary grouping size of the integer part
    secondaryGroupSize: 0,
    // grouping separator of the fraction part
    fractionGroupSeparator: ' ',
    // grouping size of the fraction part
    fractionGroupSize: 0,
    // string to append
    suffix: ''
  };
 * toFormat(12345.34567, 2, 1, {
    ...fmt,
    groupSize: 4,
    groupSeparator: '_'
  })
 * // 1_2345.34 you can custom fmt prop to return custom str
 *
 * toFormat(12345.34567, {
    ...fmt,
    groupSize: 4,
    groupSeparator: '_'
  })
 * // 1_2345.34567

 * @returns { string } res
 * @link default fmt config  https://mikemcl.github.io/bignumber.js/#format
 * @link bigNumber toFormat  https://mikemcl.github.io/bignumber.js/#toFor
 */

export function toFormat(
  v: BigNumber.Value,
  decimalPlaces: number,
  roundingMode: BigNumber.RoundingMode,
  format?: BigNumber.Format
): string;
export function toFormat(
  v: BigNumber.Value,
  decimalPlaces: number,
  roundingMode?: BigNumber.RoundingMode
): string;
export function toFormat(v: BigNumber.Value, decimalPlaces?: number): string;
export function toFormat(
  v: BigNumber.Value,
  decimalPlaces: number,
  format: BigNumber.Format
): string;
export function toFormat(v: BigNumber.Value, format: BigNumber.Format): string;

export function toFormat(v: BigNumber.Value, ...props: any[]): string {
  if (!v && v !== 0) return '';
  return new BigNumber(v).toFormat(...props);
}

/**
 * To format kilobits. and toFixedCeil decimalPlaces
 * @param num Target value
 * @param decimalPlaces Accuracy value. default retains 2 decimal places
 * @example
 * toFormatCeil(12345.34567) // 12,345.35
 * toFormatCeil(12345.34567, 4) // 12,345.3457
 * toFormatCeil(12345.34567, 4, { groupSeparator: '-' }) // 12-345.3457
 * @returns { string } result
 */
export function toFormatCeil(
  v: BigNumber.Value,
  decimalPlaces = 2,
  format?: UtilsNumberFormat
): string {
  if (!v && v !== 0) return '';
  const { removeZero = true, ...fmt } = format ?? {};
  // const dc = getRealDecimalPlaces(v, decimalPlaces, removeZero)
  // return toFormat(v, dc, 2, { ...BIG_NUMBER_FORMAT, ...fmt })
  const res = toFixedCeil(v, decimalPlaces, removeZero);
  const dc = getRealDecimalPlaces(res, decimalPlaces, removeZero);
  return toFormat(res, dc, { ...BIG_NUMBER_FORMAT, ...fmt });
}

/**
 * To format kilobits. and toFixedFloor decimalPlaces
 * @param num Target value
 * @param decimalPlaces Accuracy value. default retains 2 decimal places
 * @example
 * toFormatFloor(12345.34567) // 12,345.34
 * toFormatFloor(12345.34567, 4) // 12,345.3456
 * toFormatFloor(12345.34567, 4, { groupSeparator: '-' }) // 12-345.3456
 * @returns { string } result
 */
export function toFormatFloor(
  v: BigNumber.Value,
  decimalPlaces = 2,
  format?: UtilsNumberFormat
): string {
  if (!v && v !== 0) return '';
  const { removeZero = true, ...fmt } = format ?? {};
  // const dc = getRealDecimalPlaces(v, decimalPlaces, removeZero)
  // return toFormat(v, dc, 3, { ...BIG_NUMBER_FORMAT, ...fmt })
  const res = toFixedFloor(v, decimalPlaces, removeZero);
  const dc = getRealDecimalPlaces(res, decimalPlaces, removeZero);
  return toFormat(res, dc, { ...BIG_NUMBER_FORMAT, ...fmt });
}

/**
 * To format kilobits. and toFormatRound decimalPlaces
 * @param num Target value
 * @param decimalPlaces Accuracy value. default retains 2 decimal places
 * @example
 * toFormatRound(12345.34567) //  12,345.35
 * toFormatRound(12345.34567, 4) // 12,345.3457
 * toFormatRound(12345.34567, 1) // 12,345.3
 * toFormatRound(12345.34567, 1, { groupSeparator: '-' }) // 12-345.3
 * @returns { string } result
 */
export function toFormatRound(
  v: BigNumber.Value,
  decimalPlaces = 2,
  format?: UtilsNumberFormat
): string {
  if (!v && v !== 0) return '';
  const { removeZero = true, ...fmt } = format ?? {};
  // const dc = getRealDecimalPlaces(v, decimalPlaces, removeZero)
  // return toFormat(v, dc, 4, { ...BIG_NUMBER_FORMAT, ...fmt })
  const res = toFixedRound(v, decimalPlaces, removeZero);
  const dc = getRealDecimalPlaces(res, decimalPlaces, removeZero);
  return toFormat(res, dc, { ...BIG_NUMBER_FORMAT, ...fmt });
}

/**
 * To format kilobits. and toIntercept decimalPlaces
 * @param num Target value
 * @param decimalPlaces Accuracy value. default retains 2 decimal places
 * @example
 * toFormatIntercept(12345.34567) //  12,345.34
 * toFormatIntercept(12345.34567, 4) // 12,345.3456
 * toFormatIntercept(12345.34567, 4, { groupSeparator: '-' }) // 12-345.3456
 * @returns { string } result
 */
export function toFormatIntercept(
  v: BigNumber.Value,
  decimalPlaces = 2,
  format?: UtilsNumberFormat
): string {
  if (!v && v !== 0) return '';
  const { removeZero = true, ...fmt } = format ?? {};
  // const dc = getRealDecimalPlaces(v, decimalPlaces, removeZero)
  // return toFormat(toIntercept(v, dc), dc, { ...BIG_NUMBER_FORMAT, ...fmt })
  const res = toIntercept(v, decimalPlaces, removeZero);
  const dc = getRealDecimalPlaces(res, decimalPlaces, removeZero);
  return toFormat(res, dc, { ...BIG_NUMBER_FORMAT, ...fmt });
}

/**
 * A is greater than B
 * @param A
 * @param B
 * @returns { boolean }
 */
export const isGreaterThan = (
  a: BigNumber.Value,
  b: BigNumber.Value
): boolean => new BigNumber(a).isGreaterThan(b);

/**
 * A is greater than or equal B
 * @param A
 * @param B
 * @returns { boolean }
 *
 */
export const isGreaterThanOrEqualTo = (
  a: BigNumber.Value,
  b: BigNumber.Value
): boolean => new BigNumber(a).isGreaterThanOrEqualTo(b);

/**
 * A is less than or equal B
 * @param A
 * @param B
 * @returns { boolean }
 */
export const isLessThanOrEqualTo = (
  a: BigNumber.Value,
  b: BigNumber.Value
): boolean => new BigNumber(a).isLessThanOrEqualTo(b);

/**
 * A is less than B
 * @param A
 * @param B
 * @returns { boolean }
 */
export const isLessThan = (a: BigNumber.Value, b: BigNumber.Value): boolean =>
  new BigNumber(a).isLessThan(b);

/**
 * A is equal B
 * @param A
 * @param B
 * @returns { boolean }
 * @example
 * isEqualTo(200, '200')    // true
 * isEqualTo(0, '-0')   // true
 */
export const isEqualTo = (a: BigNumber.Value, b: BigNumber.Value): boolean =>
  new BigNumber(a).isEqualTo(b);

/**
 * simplize to the single letter number
 * @param n
 * @param p decimal places, default is 3
 * @param prefix $ ¥
 * @param mode BigNumber.RoundingMode, default is ROUND_DOWN
 * @param benchmark default is 1000
 * @param unit default is ['', 'K', 'M', 'B', 'T', 'Q']
 * @returns { string }
 * @example
 * simplize(20000)    // 20.000K
 * simplize(20000, 0)    // 20K
 * simplize(42230550, 5)   // 42.23055M
 * simplize(42230550, 2, '¥', 0, 10000, ['', '万', '亿', '兆'])   // ¥4223.06万
 */
export const simplize = (
  n: number | string | BigNumber.Value,
  p = 3,
  prefix = '',
  mode: BigNumber.RoundingMode = BigNumber.ROUND_DOWN,
  benchmark = 1000,
  unit = ['', 'K', 'M', 'B', 'T', 'Q']
) => {
  let x = new BigNumber(n);
  let i = 0;

  while (x.isGreaterThanOrEqualTo(benchmark) && !!unit[i + 1]) {
    x = x.dividedBy(benchmark);
    x = new BigNumber(x);
    i++;
  }

  return prefix + (x.toFixed(p, mode).toString() + unit[i]);
};

/**
 * @param numerator 分子
 * @param denominator 分母
 * @returns return '+' OR '-' OR '' before number
 * @example
 * getPercentageSymbol(1, 2)  // '+'
 * getPercentageSymbol(-1, 2)   // '-'
 */
const getPercentageSymbol = (numerator: number, denominator: number) => {
  if (numerator > 0) {
    if (denominator > 0) {
      return POSITIVE_SYMBOL;
    }
    return NEGATIVE_SYMBOL;
  }
  if (numerator < 0) {
    if (denominator > 0) {
      return NEGATIVE_SYMBOL;
    }
    return POSITIVE_SYMBOL;
  }
  return '';
};

/**
 * get percentage number
 * @param numerator
 * @param denominator
 * @param config
 * @returns
 */
export const percentage = (
  numerator: number,
  denominator: number,
  config?: {
    digit?: number;
    withSymbol?: boolean;
    allowNegative?: boolean;
    format?: boolean;
  }
) => {
  const {
    digit = 2,
    withSymbol = false,
    allowNegative = false,
    format = false,
  } = config ?? {};
  const symbol = withSymbol ? getPercentageSymbol(numerator, denominator) : '';
  const diff = allowNegative
    ? numerator / denominator
    : Math.abs(numerator / denominator);
  return denominator === 0
    ? DASH
    : `${symbol}${
        format
          ? toFormatRound(diff * 100, digit, { removeZero: false })
          : toFixedRound(diff * 100, digit, false)
      }%`;
};

/**
 *
 * @param v
 * @returns
 */
export const removeComma = (v: string) => String(v).replaceAll(',', '');

/**
 *  add comma
 *  Used in input scenarios
 *
 */
export const addComma = (v: string) => {
  try {
    let reg = '\\d(?=(\\d{3})+$)';
    if (String(v).indexOf('.') > -1) {
      reg = '\\d(?=(\\d{3})+\\.)';
    }
    return v.toString().replace(getSafeRegex(reg, 'g'), '$&,');
  } catch (err) {
    return 0;
  }
};
